import {
  HttpClient,
  robotoHeaders,
  type PaginatedResponse,
} from "@/shared/http";
import { parseTimestampsAsBigInt } from "@/shared/json";

import { Association, type AssociationRecord } from "../association";

import {
  type MessagePathRecord,
  type MessagePathRepresentationMapping,
} from "./MessagePathRecord";
import {
  type SchemaFieldRecord,
  type TopicRecord,
  type TopicTimeBounds,
} from "./TopicRecord";

export class TopicService {
  private readonly httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.httpClient = httpClient;
  }

  public async getMessagePathById(
    messagePathId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
    }>,
  ): Promise<MessagePathRecord> {
    const url = this.httpClient.constructUrl(
      `v1/topics/message-path/id/${messagePathId}`,
    );
    const response = await this.httpClient.get(url, {
      signal: options?.abortSignal,
    });
    return response.json<MessagePathRecord>();
  }

  public async getMessagePathRepresentations(
    topicId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
      resourceOwnerId: string;
    }>,
  ): Promise<MessagePathRepresentationMapping[]> {
    const url = this.httpClient.constructUrl(
      `v1/topics/id/${topicId}/message-path/representations`,
    );
    const response = await this.httpClient.get(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });
    return response.json<MessagePathRepresentationMapping[]>();
  }

  /**
   * Every distinct `topic_name.message_path` string in the org, capped
   * server-side at 10,000 entries.
   *
   * For the largest orgs this request can time out with a 504, and the cap
   * covers only part of their message paths. Topic names come from the
   * separate {@link getTopicNamesForOrg}, so a caller that also wants names
   * does not wait on this request for them.
   */
  public async getMessagePathsForOrg(
    options?: Partial<{
      abortSignal: AbortSignal;
      resourceOwnerId: string;
      /**
       * Return only values starting with this, matched against the whole
       * `topic_name.message_path` string. Send what the user has typed:
       * without it the server's cap yields an alphabetical head of the corpus,
       * so for a large org anything sorting after the cut cannot be completed
       * to at all, however much the client filters.
       */
      prefix: string;
    }>,
  ): Promise<string[]> {
    if (!options?.resourceOwnerId) {
      throw Error(
        "getMessagePathsForOrg requires an org ID, none was provided",
      );
    }

    const params = new URLSearchParams();
    if (options.prefix) {
      params.set("prefix", options.prefix);
    }
    const requestUrl = this.httpClient.constructUrl(
      "v1/topics/unique/topic_message_paths",
      params,
    );
    const response = await this.httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
    });
    return response.json<string[]>();
  }

  /**
   * Every field a topic schema declares, via
   * `GET v2/topics/schema/id/{schema_id}/fields`. A struct and each of its
   * children are separate records. Schemas are content-addressed, so a
   * schema id's fields never change.
   */
  public async getSchemaFields(
    schemaId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
      resourceOwnerId: string;
    }>,
  ): Promise<SchemaFieldRecord[]> {
    const url = this.httpClient.constructUrl(
      `v2/topics/schema/id/${schemaId}/fields`,
    );
    const response = await this.httpClient.get(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });
    return response.json<SchemaFieldRecord[]>();
  }

  /**
   * The time span covered by every topic of the file or dataset named by
   * `association`, computed by the server in one request instead of paging
   * through {@link getTopicsByAssociation}.
   */
  public async getTimeBoundsForAssociation(
    association: AssociationRecord,
    options?: Partial<{
      abortSignal: AbortSignal;
      resourceOwnerId: string;
    }>,
  ): Promise<TopicTimeBounds> {
    const encodedAssociation = new Association(association).urlEncode();
    const url = this.httpClient.constructUrl(
      `v1/topics/association/${encodedAssociation}/time-bounds`,
    );
    const response = await this.httpClient.get(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });
    return response.json<TopicTimeBounds>(parseTimestampsAsBigInt);
  }

  /**
   * The time span covered by every topic under `datasetId`, computed by the
   * server in one request.
   *
   * Answers "when does this dataset start?" without paging through every
   * topic of every file with {@link getTopicsForDataset} to find the smallest
   * `start_time`.
   */
  public async getTimeBoundsForDataset(
    datasetId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
      resourceOwnerId: string;
    }>,
  ): Promise<TopicTimeBounds> {
    const url = this.httpClient.constructUrl(
      `v1/datasets/${datasetId}/topics/time-bounds`,
    );
    const response = await this.httpClient.get(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });
    return response.json<TopicTimeBounds>(parseTimestampsAsBigInt);
  }

  public async getTopicById(
    topicId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
    }>,
  ): Promise<TopicRecord> {
    const url = this.httpClient.constructUrl(`v1/topics/id/${topicId}`);
    const response = await this.httpClient.get(url, {
      signal: options?.abortSignal,
    });
    return response.json<TopicRecord>(parseTimestampsAsBigInt);
  }

  public async getTopicByNameAndAssociation(
    topicName: string,
    association: AssociationRecord,
    options?: Partial<{
      abortSignal: AbortSignal;
      resourceOwnerId: string;
    }>,
  ): Promise<TopicRecord> {
    const encodedAssociation = new Association(association).urlEncode();
    const encodedTopicName = encodeURIComponent(topicName);
    const url = this.httpClient.constructUrl(
      `v1/topics/association/${encodedAssociation}/name/${encodedTopicName}`,
    );
    const response = await this.httpClient.get(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });
    return response.json<TopicRecord>(parseTimestampsAsBigInt);
  }

  /**
   * Distinct field paths (dot-joined, e.g. `linear_acceleration.x`) of one
   * topic, looked up by name, so field autocomplete offers that topic's fields
   * rather than every message path in the org. Returns `[]` for a topic with
   * no rows in the server's normalized schema tables, such as one ingested
   * before those tables existed.
   */
  public async getTopicFields(
    topicName: string,
    options?: Partial<{
      abortSignal: AbortSignal;
      resourceOwnerId: string;
    }>,
  ): Promise<string[]> {
    if (!options?.resourceOwnerId) {
      throw Error("getTopicFields requires an org ID, none was provided");
    }

    const requestUrl = this.httpClient.constructUrl(
      "v1/topics/unique/topic_fields",
      new URLSearchParams({ topic_name: topicName }),
    );
    const response = await this.httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
    });

    return response.json<string[]>();
  }

  /**
   * Every topic name in the org.
   *
   * A separate request from {@link getMessagePathsForOrg}, which can time out
   * for the largest orgs, so a caller that needs only names is neither slowed
   * nor failed by it. The skill editor, for one, resolves its `[[topic]]`
   * references against these names and fetches a referenced topic's fields
   * with {@link getTopicFields}.
   */
  public async getTopicNamesForOrg(
    options?: Partial<{
      abortSignal: AbortSignal;
      resourceOwnerId: string;
    }>,
  ): Promise<string[]> {
    if (!options?.resourceOwnerId) {
      throw Error("getTopicNamesForOrg requires an org ID, none was provided");
    }

    const requestUrl = this.httpClient.constructUrl(
      "v1/topics/unique/topic_names",
    );
    const response = await this.httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
    });
    return response.json<string[]>();
  }

  public async getTopicsByAssociation(
    association: AssociationRecord,
    options?: Partial<{
      abortSignal: AbortSignal;
      pageToken: string;
      resourceOwnerId: string;
    }>,
  ): Promise<TopicRecord[]> {
    const assoc = new Association(association);
    const paginatedResponse = await this.getPagedTopicsByAssociation(
      assoc,
      options,
    );
    const topicRecords = [...paginatedResponse.items];

    let nextPageToken = paginatedResponse.next_token;
    while (nextPageToken) {
      const nextPaginatedResponse = await this.getPagedTopicsByAssociation(
        assoc,
        { ...options, pageToken: nextPageToken },
      );
      topicRecords.push(...nextPaginatedResponse.items);
      nextPageToken = nextPaginatedResponse.next_token;
    }

    return topicRecords;
  }

  /**
   * Gets all the topics for every file inside a given dataset.
   */
  public async getTopicsForDataset(
    datasetId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
      resourceOwnerId: string;
    }>,
  ): Promise<TopicRecord[]> {
    const topicRecords: TopicRecord[] = [];
    let pageToken: string | undefined;

    do {
      const searchParams = new URLSearchParams();
      if (pageToken) {
        searchParams.append("page_token", pageToken);
      }

      const url = this.httpClient.constructUrl(
        `v1/datasets/${datasetId}/topics`,
        searchParams,
      );

      const response = await this.httpClient.get(url, {
        signal: options?.abortSignal,
        headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
      });

      const paginatedResponse = await response.json<
        PaginatedResponse<TopicRecord>
      >(parseTimestampsAsBigInt);

      topicRecords.push(...paginatedResponse.items);
      pageToken = paginatedResponse.next_token ?? undefined;
    } while (pageToken);

    return topicRecords;
  }

  private async getPagedTopicsByAssociation(
    association: Association,
    options?: Partial<{
      abortSignal: AbortSignal;
      pageToken: string;
      resourceOwnerId: string;
    }>,
  ): Promise<PaginatedResponse<TopicRecord>> {
    const encodedAssociation = association.urlEncode();
    const searchParams = new URLSearchParams();
    if (options?.pageToken !== undefined) {
      searchParams.append("page_token", options.pageToken);
    }
    const url = this.httpClient.constructUrl(
      `v1/topics/association/${encodedAssociation}`,
      searchParams,
    );
    const response = await this.httpClient.get(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });
    return response.json<PaginatedResponse<TopicRecord>>(
      parseTimestampsAsBigInt,
    );
  }
}
